import { useEffect, useState } from "react";
import { useExitDelay } from "../../lib/useExitDelay";
import { Icon } from "./Icon";

// The small pieces every Maruti Service Desk page is built from, rebuilt from
// design/maruti-service-desk3.html. Each value is the prototype's final computed
// value, so a page that uses these needs no spacing of its own for them.
// They read the `.msd` design tokens, so they only work inside MsdShell.

export const cx = (...parts) => parts.filter(Boolean).join(" ");

export const DISP = "tracking-[-0.02em]";

// .card
export const CARD = "rounded-[20px] bg-msd-surface shadow-msd-e2";
// .lbl / .secl
export const LBL = "text-[13px] text-msd-muted";
export const SECL = "text-[12px] font-semibold tracking-[.06em] text-msd-faint";

const LIFT =
  "hover:shadow-[0_0_0_.5px_rgba(16,24,40,.06),0_2px_4px_rgba(16,24,40,.08),0_6px_14px_rgba(16,24,40,.08)]";
const SOLID_SHADOW = "shadow-[0_1px_2px_rgba(16,24,40,.14)]";

const BTN_VARIANT = {
  plain: `bg-msd-surface text-msd-ink shadow-msd-e1 ${LIFT}`,
  pri: `bg-msd-btn text-white ${SOLID_SHADOW}`,
  dark: `bg-msd-btn text-white ${SOLID_SHADOW}`,
  red: `bg-msd-brick text-white ${SOLID_SHADOW}`,
};

// .btn, .btn.pri, .btn.dark, .btn.red, .btn.sm
export function Btn({ variant = "plain", size, icon, className, children, type = "button", ...rest }) {
  return (
    <button
      type={type}
      className={cx(
        "inline-flex items-center justify-center gap-2 whitespace-nowrap border-0 font-semibold disabled:cursor-not-allowed disabled:opacity-45",
        size === "sm" ? "h-[34px] rounded-[10px] px-3 text-[13px]" : "h-10 rounded-xl px-[14px] text-[14px]",
        BTN_VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {icon ? <Icon name={icon} size={16} stroke={2} /> : null}
      {children}
    </button>
  );
}

// .ibtn
export function IconBtn({ icon, label, size = 20, stroke = 1.8, className, type = "button", ...rest }) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        `inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-0 bg-msd-surface text-msd-ink shadow-msd-e1 ${LIFT}`,
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size} stroke={stroke} />
    </button>
  );
}

// .link
export function LinkBtn({ className, children, type = "button", ...rest }) {
  return (
    <button
      type={type}
      className={cx("inline-flex items-center gap-[6px] border-0 bg-transparent p-0 text-[14px] font-semibold text-msd-royal", className)}
      {...rest}
    >
      {children}
    </button>
  );
}

// Capsules are neutral; the colour lives only in the dot or the icon.
const TONE = {
  red: "#F04438",
  brick: "#F04438",
  purple: "#F04438",
  orange: "#F79009",
  amber: "#F79009",
  green: "#17B26A",
  pine: "#17B26A",
  blue: "#3A5F94",
  royal: "#3A5F94",
  teal: "#3A5F94",
  grey: "#3A5F94",
};
export const toneColor = (tone, fallback = "#8E8E93") => TONE[tone] || fallback;

const CAPSULE = "inline-flex items-center whitespace-nowrap rounded-full bg-[rgba(120,120,128,.12)] text-msd-ink [:root[data-theme=dark]_&]:bg-white/10";

function CapsuleMark({ icon, color, iconSize }) {
  return icon ? (
    <Icon name={icon} size={iconSize} stroke={2} color={color} className="shrink-0" />
  ) : (
    <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: color }} />
  );
}

// .tag  (small = the 22px variant used under chat bubbles and journey rows)
export function Tag({ tone, icon, small = false, className, children, ...rest }) {
  return (
    <span
      className={cx(
        CAPSULE,
        "gap-[5px] font-medium",
        small ? "h-[22px] pl-2 pr-[9px] text-[12px]" : "h-6 px-[10px] text-[12px]",
        className,
      )}
      {...rest}
    >
      <CapsuleMark icon={icon} color={toneColor(tone)} iconSize={13} />
      {children}
    </span>
  );
}

// .chip — a capsule you can press
export function Chip({ tone, icon, className, children, type = "button", ...rest }) {
  return (
    <button
      type={type}
      className={cx(CAPSULE, "h-7 gap-[6px] border-0 px-3 text-[13px] font-medium hover:brightness-[.97]", className)}
      {...rest}
    >
      <CapsuleMark icon={icon} color={toneColor(tone, "#F79009")} iconSize={14} />
      {children}
    </button>
  );
}

// .dot
export const Dot = ({ color, className }) => (
  <span className={cx("inline-block h-2 w-2 shrink-0 rounded-full", className)} style={{ background: color }} />
);

// .pill, .pill.on, .pill .n
export function Pill({ on = false, count, className, children, type = "button", ...rest }) {
  return (
    <button
      type={type}
      aria-pressed={on}
      className={cx(
        "inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full border-0 px-[14px] text-[14px] font-semibold",
        on ? "bg-msd-btn text-white shadow-[0_2px_8px_rgba(16,24,40,.18)]" : "bg-msd-surface text-msd-ink shadow-msd-e1",
        className,
      )}
      {...rest}
    >
      {children}
      {count != null ? (
        <span className={cx("rounded-full px-2 py-px text-[12px]", on ? "bg-white/[.18]" : "bg-[rgba(120,120,128,.12)]")}>{count}</span>
      ) : null}
    </button>
  );
}

// .seg2 (and .seg2.sm): a segmented control. options: [[value, label], ...]
export function Seg2({ options, value, onChange, label, small = false, testid }) {
  return (
    <div className="inline-flex rounded-xl bg-msd-surface p-[3px] shadow-msd-e1" role="group" aria-label={label} data-testid={testid}>
      {options.map(([k, text]) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          data-testid={testid ? `${testid}-${k}` : undefined}
          className={cx(
            "rounded-[9px] border-0",
            small ? "h-7 px-[10px] text-[13px]" : "h-8 px-[14px] text-[14px]",
            value === k ? "bg-msd-btn font-semibold text-white" : "bg-transparent text-msd-muted",
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

// .pg — the column a non-analytics page lays its sections out in
export function Page({ className, children, ...rest }) {
  return (
    <div className={cx("flex flex-col gap-6 [&>*]:shrink-0", className)} {...rest}>
      {children}
    </div>
  );
}

// .pg-head — title, one line under it, and the page's actions on the right
export function PageHead({ title, sub, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className={cx("m-0 text-[24px] font-semibold leading-8 text-msd-ink", DISP)}>{title}</h1>
        {sub ? <div className="mt-1 text-[14px] text-msd-muted">{sub}</div> : null}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

// .pp-search (the search box that sits on the page ground)
export function SearchBox({ value, onChange, placeholder, className, testid, ...rest }) {
  return (
    <label
      className={cx(
        "flex h-[42px] max-w-[360px] flex-[1_1_240px] items-center gap-2 rounded-xl bg-msd-surface px-3 text-msd-muted shadow-msd-e1 focus-within:shadow-[0_0_0_2px_#3A5F94]",
        className,
      )}
    >
      <Icon name="search" size={18} />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        data-testid={testid}
        className="min-w-0 grow border-0 bg-transparent text-[15px] text-msd-ink outline-none placeholder:text-msd-muted"
        {...rest}
      />
    </label>
  );
}

// .pp-sel
export function SelectBox({ value, onChange, label, className, children, testid, ...rest }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      data-testid={testid}
      className={cx("h-[42px] rounded-xl border-0 bg-msd-surface px-3 text-[14px] text-msd-ink shadow-msd-e1", className)}
      {...rest}
    >
      {children}
    </select>
  );
}

// .empty
export function Empty({ title, className, children }) {
  return (
    <div className={cx("rounded-[14px] border border-dashed border-msd-line2 p-[22px] text-center text-[14px] text-msd-muted", className)}>
      {title ? <div className="mb-[6px] font-bold text-msd-ink">{title}</div> : null}
      {children}
    </div>
  );
}

// .av — a round badge with someone's initials
export const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export function Avatar({ name, size = 40, className }) {
  return (
    <span
      className={cx("flex shrink-0 items-center justify-center rounded-full bg-msd-soft font-bold text-msd-ink2", className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.33) }}
      aria-hidden="true"
    >
      {initialsOf(name) || "–"}
    </span>
  );
}

// .in — form controls sit on the page ground, with a blue ring on focus
export const INPUT =
  "h-[46px] w-full min-w-0 rounded-xl border-0 bg-msd-ground px-[14px] text-[16px] text-msd-ink outline-none placeholder:text-msd-faint focus:shadow-[0_0_0_2px_#3A5F94] disabled:opacity-60";

// .fl, .fl-l, .fl-h — a labelled field with an optional hint or error under it
export function Field({ label, hint, error, optional = false, className, children }) {
  return (
    <label className={cx("flex flex-col gap-[7px]", className)}>
      <span className="text-[13.5px] font-semibold text-msd-ink2">
        {label}
        {optional ? <span className="font-normal text-msd-muted"> optional</span> : null}
      </span>
      {children}
      {error ? (
        <span className="text-[12.5px] text-msd-brick-ink" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="text-[12.5px] text-msd-muted">{hint}</span>
      ) : null}
    </label>
  );
}

// .sw — an on/off switch
export function Switch({ on, onChange, label, disabled = false, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={cx(
        "relative h-[30px] w-[50px] shrink-0 rounded-full border-0 p-0 transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50",
        on ? "bg-[#17B26A]" : "bg-[rgba(120,120,128,.24)]",
      )}
    >
      <i
        className={cx(
          "absolute top-[2px] h-[26px] w-[26px] rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,.2)] transition-[left] duration-150",
          on ? "left-[22px]" : "left-[2px]",
        )}
      />
    </button>
  );
}

// .sheet, .sh-h, .sh-b, .sh-f — the panel that slides over the right edge to add
// or edit something. Escape and the scrim close it. `onSubmit` makes the body a
// form, so Enter saves.
export function Sheet({ open, title, onClose, onSubmit, footer, children, testid }) {
  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [open, onClose]);
  if (!open) return null;
  const Body = onSubmit ? "form" : "div";
  return (
    // Portalled content would lose the `.msd` tokens, so the sheet renders in place.
    <>
      <div className="fixed inset-0 z-50 bg-[rgba(15,21,48,.18)]" onClick={onClose} />
      <Body
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        data-testid={testid}
        onSubmit={
          onSubmit
            ? (e) => {
                e.preventDefault();
                onSubmit(e);
              }
            : undefined
        }
        className="fixed bottom-3 right-3 top-3 z-[70] flex w-[min(500px,calc(100vw-24px))] flex-col overflow-hidden rounded-[22px] bg-msd-surface text-msd-ink shadow-msd-pop"
      >
        <div className="flex items-center justify-between px-[22px] pb-2 pt-5">
          <div className="text-[20px] font-bold tracking-[-0.015em]">{title}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-msd-muted hover:bg-msd-royal-soft"
          >
            <Icon name="x" size={18} stroke={2.2} />
          </button>
        </div>
        <div className="flex grow flex-col gap-[18px] overflow-auto px-[22px] pb-[22px] pt-2">{children}</div>
        {footer ? <div className="flex justify-end gap-[10px] px-[22px] py-[14px] shadow-[0_-1px_0_var(--line)]">{footer}</div> : null}
      </Body>
    </>
  );
}

// Folds its content open and closed smoothly instead of switching it on and off.
// The height eases between 0 and its natural size (a grid row going 0fr -> 1fr),
// so it needs no measuring. The content stays mounted while it closes, then is
// removed, and it only clips while moving so popovers inside it are not cut off.
export function Collapse({ open, children, className }) {
  const { value: shown } = useExitDelay(open, COLLAPSE_MS);
  // Mounted closed for a frame so opening has something to animate from; a block
  // that starts open skips this and appears at once.
  const [armed, setArmed] = useState(!!open);
  const [moving, setMoving] = useState(false);
  useEffect(() => {
    setMoving(true);
    if (!open) {
      setArmed(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setArmed(true)));
    return () => cancelAnimationFrame(id);
  }, [open]);
  if (!shown) return null;
  const expanded = open && armed;
  return (
    <div
      onTransitionEnd={(e) => e.target === e.currentTarget && setMoving(false)}
      className={cx(
        "grid transition-[grid-template-rows,opacity] duration-[260ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none",
        expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className={cx("min-h-0", (moving || !expanded) && "overflow-hidden", className)}>{children}</div>
    </div>
  );
}
const COLLAPSE_MS = 260;
